import React from "react";
import { toast } from "react-hot-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Edit3, X, Check } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useUpdateTeam } from "@/hooks/api-hooks";

const editTeamSchema = z.object({
  name: z.string().min(2, "Team name must be at least 2 characters"),
  description: z.string().min(10, "Description must be at least 10 characters"),
});

type EditTeamFormData = z.infer<typeof editTeamSchema>;

interface Team {
  id: string;
  name: string;
  description: string;
}

interface EditTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: Team | null;
  onSuccess?: () => void;
}

export function EditTeamModal({
  isOpen,
  onClose,
  team,
  onSuccess
}: EditTeamModalProps) {
  const updateTeamMutation = useUpdateTeam();

  const form = useForm<EditTeamFormData>({
    resolver: zodResolver(editTeamSchema),
    defaultValues: {
      name: team?.name || "",
      description: team?.description || "",
    },
  });

  // Update form when team changes
  React.useEffect(() => {
    if (team) {
      form.reset({
        name: team.name,
        description: team.description,
      });
    }
  }, [team, form]);

  const onSubmit = async (data: EditTeamFormData) => {
    if (!team) return;

    updateTeamMutation.mutate(
      { id: team.id, ...data },
      {
        onSuccess: () => {
          toast.success(`Team "${data.name}" updated successfully!`);

          onSuccess?.();
          onClose();
          form.reset();
        },
        onError: (error: any) => {
          console.error("Error updating team:", error);
          toast.error(`Failed to update team: ${error?.message || 'Please try again.'}`);
        }
      }
    );
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg bg-card text-card-foreground border border-border shadow-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Edit3 className="h-5 w-5 text-white" />
            </div>
            <DialogTitle className="text-xl font-bold text-foreground">
              Edit Team
            </DialogTitle>
          </div>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Team Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter team name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description *</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Describe what this team does..."
                        rows={4}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={updateTeamMutation.isPending}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateTeamMutation.isPending}
                className="bg-primary hover:bg-primary/90"
              >
                {updateTeamMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Update Team
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}